import type { SignatureAccessorialType } from '@fv/client-types'
import type { LocationAccessorialType, Workflow } from '@fv/models'

import CheckboxField from '@/components/inputs/CheckboxField'
import { InputGroup } from '@/components/inputs/InputGroup'
import type { RadioFieldOption } from '@/components/inputs/RadioField'
import { Pinline } from '@/components/Pinline'
import { hasInsideOption, hasLiftgateOption } from '@/utils/locationFuncs'

import type { ShipmentLocationFormModel } from './types'

type Props = {
  workflow: Workflow
  value: ShipmentLocationFormModel
  disabled?: boolean
  onChange: (v: ShipmentLocationFormModel) => void
}
export const LocationAccessorialForm = (props: Props) => {
  const { workflow, value } = props

  if (workflow === 'ltl' && value.type === 'origin') {
    return <LtlOriginAccessorials {...props} />
  } else if (workflow === 'ltl' && value.type === 'destination') {
    return <LtlDestinationAccessorials {...props} />
  } else if (workflow === 'parcel' && value.type === 'origin') {
    return <ParcelOriginAccessorials {...props} />
  } else if (workflow === 'parcel' && value.type === 'destination') {
    return <ParcelDestinationAccessorials {...props} />
  }
  return null
}

const ParcelDestinationAccessorials = ({
  value,
  disabled,
  onChange,
}: Props) => {
  return (
    <>
      <div className="form-group border-b border-dashed border-b-[#99d87a] pb-3">
        <CheckboxField
          disabled={disabled}
          onChange={() => onChange(toggleResidential(value))}
          checked={value.shipType === 'residential'}
          name={`${value.type}.residential`}
          label="Residential location"
        />
      </div>

      <InputGroup
        className="b1600:basis-1/2 basis-1/3"
        inputType="radio"
        label="Signature Required"
        inputProps={{
          name: 'signature',
          value:
            value.accessorials.find(a => a.key.includes('signature'))?.key ??
            '',
          onChange: e => {
            onChange(
              updateSignatureAccessorial(
                value,
                e.target.value as SignatureAccessorialType,
              ),
            )
          },
          options: [
            { label: 'No', value: '' },
            { label: 'Yes', value: 'signature-required' },
            {
              label: 'Adult',
              value: 'adult-signature-required',
            },
            ...(value.shipType === 'residential'
              ? [
                  {
                    label: 'Indirect',
                    value: 'indirect-signature-required',
                  },
                ]
              : []),
          ],
        }}
      />
    </>
  )
}

const LtlDestinationAccessorials = ({ value, disabled, onChange }: Props) => {
  const hasDestInsideOption = hasInsideOption(value.shipType)
  const hasDestLiftgateOption = hasLiftgateOption(value.shipType)
  return (
    <>
      {hasDestLiftgateOption && (
        <CheckboxField
          disabled={disabled}
          {...getCheckboxProps(value, 'liftgate', onChange)}
          label="Liftgate required for delivery?"
        />
      )}

      {hasDestInsideOption && (
        <CheckboxField
          disabled={disabled}
          {...getCheckboxProps(value, 'inside', onChange)}
          label="Inside delivery required?"
        />
      )}

      {(hasDestInsideOption || hasDestLiftgateOption) && <Pinline rule />}
      <InputGroup
        inputType="radio"
        label="Delivery preference"
        inputProps={{
          name: 'delivery-preference',
          value:
            value.accessorials.find(
              a => a.key === 'notify' || a.key === 'schedule',
            )?.key ?? '',
          onChange: e => {
            onChange(
              updateDeliveryPreferenceAccessorial(
                value,
                e.target.value as DeliveryPreference,
              ),
            )
          },
          options: deliveryPreferenceOptions,
        }}
      />

      <CheckboxField
        disabled={disabled}
        {...getCheckboxProps(value, 'sort-and-segregate', onChange)}
        label="Sort and segregate"
      />
    </>
  )
}

const ParcelOriginAccessorials = ({ value, disabled, onChange }: Props) => {
  return (
    <div className="mx-1 mb-4 basis-full border-b border-dashed border-b-gray-400 pb-3">
      <CheckboxField
        disabled={disabled}
        onChange={() => onChange(toggleResidential(value))}
        checked={value.shipType === 'residential'}
        name={`${value.type}.residential`}
        label="Residential location"
      />
    </div>
  )
}

const LtlOriginAccessorials = ({
  workflow,
  value,
  disabled,
  onChange,
}: Props) => {
  const hasOriginInsideOption = hasInsideOption(value.shipType)
  const hasOriginLiftgateOption = hasLiftgateOption(value.shipType)
  if (
    workflow !== 'ltl' ||
    !(hasOriginInsideOption || hasOriginLiftgateOption)
  ) {
    return null
  }

  return (
    <div className="form-group">
      {hasOriginLiftgateOption && (
        <CheckboxField
          disabled={disabled}
          {...getCheckboxProps(value, 'liftgate', onChange)}
          label="Liftgate required for pickup?"
        />
      )}

      {hasOriginInsideOption && (
        <CheckboxField
          disabled={disabled}
          {...getCheckboxProps(value, 'inside', onChange)}
          label="Inside pickup required?"
        />
      )}
    </div>
  )
}

const hasAccessorial = (
  value: ShipmentLocationFormModel,
  name: LocationAccessorialType,
) => value.accessorials.some(s => s.key === name)

function getCheckboxProps(
  value: ShipmentLocationFormModel,
  name: LocationAccessorialType,
  onChange?: Props['onChange'],
) {
  const residentialCheck =
    name === 'residential' && value.shipType === 'residential'
  return {
    name: `${value.type}.${name}`,
    className: '[&+&]:mt-3',
    checked: hasAccessorial(value, name) || residentialCheck,
    ...(onChange && {
      onChange: () => onChange(toggleAccessorial(value, name)),
    }),
  }
}

const toggleResidential = (value: ShipmentLocationFormModel) => {
  const nextValue = { ...value }
  const stopType = value.type
  const isResidential = nextValue.shipType === 'residential'

  nextValue.shipType = isResidential ? 'business dock' : 'residential'
  if (isResidential && stopType === 'destination') {
    nextValue.accessorials = nextValue.accessorials.filter(
      a => a.key !== 'indirect-signature-required',
    )
  }
  return nextValue
}

const toggleAccessorial = (
  value: ShipmentLocationFormModel,
  accessorial: LocationAccessorialType,
) => {
  const nextValue = { ...value }
  const hasAccessorial = value.accessorials.some(a => a.key === accessorial)

  nextValue.accessorials = hasAccessorial
    ? nextValue.accessorials.filter(a => a.key !== accessorial)
    : nextValue.accessorials.concat({ key: accessorial })

  return nextValue
}

const updateSignatureAccessorial = (
  value: ShipmentLocationFormModel,
  sigAccessorial: SignatureAccessorialType,
) => {
  const nextValue = {
    ...value,
    accessorials: value.accessorials.filter(a => !a.key.includes('signature')),
  }
  if (sigAccessorial) {
    nextValue.accessorials.push({ key: sigAccessorial })
  }
  return nextValue
}

type DeliveryPreference = 'notify' | 'schedule' | ''
const deliveryPreferenceOptions: RadioFieldOption<DeliveryPreference>[] = [
  { label: 'None', value: '' },
  { label: 'Call / notify only', value: 'notify' },
  { label: 'Appointment is required', value: 'schedule' },
]
const updateDeliveryPreferenceAccessorial = (
  value: ShipmentLocationFormModel,
  preference: DeliveryPreference,
) => {
  const nextValue = {
    ...value,
    accessorials: value.accessorials.filter(
      a => a.key !== 'notify' && a.key !== 'schedule',
    ),
  }
  if (preference) {
    nextValue.accessorials.push({ key: preference })
  }
  return nextValue
}
